// مشترک نظام ارجاع ملی و سیب (AP-MED-03؛ US-MED-070 تا 076، 098 تا 100): کلیدهای Query، برچسب وضعیت و پنل‌هایی که هم در
// COMP-REFERRAL-EDITOR و COMP-RX-EDITOR و هم در SCR-STAFF-705، 707 و 716 به کار می‌روند. متن‌ها فقط از فضای نام med.
//
// ارسال **ناهمگام** است (BR-MED-076): هر دکمه فقط ردیف صف می‌سازد و کد رهگیری با تازه‌سازی نما می‌آید. محتوای بالینی
// (شکایت، تشخیص، پیام ارجاع) تنها در پنل جزئیات ارجاع ورودی دیده می‌شود و فهرست‌های عملیاتی آن را ندارند (AC-MED-076.2).
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type MedAdmissionOrderListResponse,
  type MedAdmissionOrderView,
  type MedCreateNationalReferralRequest,
  type MedEncounterTrackingCodeListResponse,
  type MedEncounterTrackingCodeView,
  type MedInboundNationalReferralView,
  type MedNationalReferralItemView,
  type MedNationalReferralListResponse,
  type MedNationalReferralScheduling,
  type MedNationalReferralStatus,
  type MedNationalReferralType,
  type MedReferralFeedbackFormResponse,
  type MedReferralSlotListResponse,
  type MedReferralSlotView,
  type MedSubmitAdmissionOrderRequest,
  type MedSubmitReferralFeedbackRequest,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  BusinessDateField,
  DataTable,
  FormDialog,
  SampleBadge,
  StatusChip,
  faDigits,
  toJalali,
  toJalaliDateTime,
  useDisplaySettings,
  type DataTableColumn,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { API } from './common';

/** ارجاع‌های ملی و کدهای رهگیری مواجهه (US-MED-070، 072، 076). */
export const SCR_STAFF_707 = 'SCR-STAFF-707';

/** دستورهای بستری تامین برای پذیرش (US-MED-098). */
export const SCR_STAFF_716 = 'SCR-STAFF-716';

export const referralKeys = {
  referrals: (tenantId: string | undefined, encounterId: string | undefined, patientId: string | undefined) =>
    ['medorders', tenantId, 'national-referrals', encounterId ?? '-', patientId ?? '-'] as const,
  slots: (tenantId: string | undefined, encounterId: string, specialty: string, center: string) =>
    ['medorders', tenantId, 'referral-slots', encounterId, specialty, center] as const,
  trackingCodes: (tenantId: string | undefined, encounterId: string) =>
    ['medorders', tenantId, 'tracking-codes', encounterId] as const,
  trackingSearch: (tenantId: string | undefined, code: string) => ['medorders', tenantId, 'tracking-search', code] as const,
  admissionOrders: (tenantId: string | undefined, encounterId: string) =>
    ['medorders', tenantId, 'admission-orders', encounterId] as const,
  inbound: (tenantId: string | undefined, inboundReferralId: string) =>
    ['medorders', tenantId, 'inbound-national', inboundReferralId] as const,
  feedbackForm: (tenantId: string | undefined, encounterId: string, system: string) =>
    ['medorders', tenantId, 'referral-feedback-form', encounterId, system] as const,
  insurerOpen: (tenantId: string | undefined, patientId: string, providerId: string) =>
    ['medorders', tenantId, 'insurer-referrals-open', patientId, providerId] as const,
  insurerAdmissions: (tenantId: string | undefined, patientId: string) =>
    ['medorders', tenantId, 'insurer-admission-orders', patientId] as const,
  hospitals: (tenantId: string | undefined) => ['medorders', tenantId, 'insurer-hospitals'] as const,
};

const STATUS_TONE: Record<MedNationalReferralStatus, StatusTone> = {
  Draft: 'inactive',
  Pending: 'pending',
  Registered: 'done',
  Failed: 'error',
};

/** برچسب وضعیت ارسال ارجاع ملی. */
export function ReferralStatusChip({ status }: { status: MedNationalReferralStatus }) {
  return <StatusChip tone={STATUS_TONE[status] ?? 'neutral'} labelKey={`med:referral_national_status.${status}`} />;
}

/** سامانه مقصد ارجاع: سیب (پیش‌فرض) یا تامین برای بیمه‌شده تامین (C-AGENT-1738). */
export const REFERRAL_SYSTEMS = ['SIB', 'TAMIN'] as const;

export type ReferralSystem = (typeof REFERRAL_SYSTEMS)[number];

function problemField(error: unknown, key: string): string | undefined {
  // افزوده‌های problem+json (مثل `field`، `reason` و `earliest`) در `ApiError.extensions` می‌نشینند.
  const value = isApiError(error) ? error.extensions[key] : undefined;
  return typeof value === 'string' ? value : undefined;
}

/** کدهای رهگیری یک مواجهه (US-MED-076؛ نمای عملیاتی بدون محتوای بالینی). */
export function useTrackingCodes(encounterId: string | undefined) {
  const { tenantApi, session } = useTenantSession();

  return useQuery({
    queryKey: referralKeys.trackingCodes(session?.tenantId, encounterId ?? '-'),
    enabled: Boolean(encounterId),
    queryFn: async (): Promise<MedEncounterTrackingCodeView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/encounters/{encounterId}/tracking-codes', {
          params: { path: { encounterId: encounterId! } },
        }),
      );
      return (response as MedEncounterTrackingCodeListResponse).items;
    },
  });
}

/**
 * فهرست و ثبت کدهای رهگیری مواجهه (SCR-STAFF-707؛ AC-MED-076.1، 076.2): نوع، کد، سامانه و زمان دریافت.
 */
export function TrackingCodesPanel({ encounterId }: { encounterId: string }) {
  const { t } = useTranslation('med');
  const { timeZone } = useDisplaySettings();
  const codes = useTrackingCodes(encounterId);

  const columns: DataTableColumn<MedEncounterTrackingCodeView>[] = [
    {
      id: 'kind',
      headerKey: 'med:referral_national.code_kind',
      cell: (row) => <Chip size="small" label={t(`med:tracking_code_kind.${row.kind}`)} data-testid={`med-tracking-kind-${row.kind}`} />,
    },
    { id: 'code', headerKey: 'med:referral_national.tracking_code', cell: (row) => <span>{row.code}</span> },
    { id: 'insurer', headerKey: 'med:referral_national.system', cell: (row) => <span>{row.insurer}</span> },
    {
      id: 'received',
      headerKey: 'med:referral_national.received_at',
      cell: (row) => <span>{toJalaliDateTime(row.receivedAt, { timeZone })}</span>,
    },
  ];

  return (
    <DataTable
      rows={codes.data}
      columns={columns}
      getRowId={(row) => `${row.kind}-${row.sourceId}`}
      isLoading={codes.isLoading}
      error={codes.error}
      onRetry={() => void codes.refetch()}
      emptyTitleKey="med:referral_national.codes_empty"
      captionKey="med:referral_national.codes_title"
    />
  );
}

/**
 * پنل ارجاع ملی یک مواجهه (COMP-REFERRAL-EDITOR و تب «ارسال» COMP-RX-EDITOR): فهرست ارجاع‌های ثبت‌شده و دیالوگ ثبت
 * ارجاع سطح ۱، افقی یا عمودی با شکایت و تشخیص کدگذاری‌شده، تاریخ ≥ روز بعد و روش «با ثبت نوبت».
 */
export function NationalReferralPanel({
  encounterId,
  patientId,
  defaultKind = 'Level1',
}: {
  encounterId: string;
  patientId?: string;
  /** نوع پیش‌فرض دیالوگ (تیک «ارجاع» بخش ارسال نسخه عمودی می‌فرستد — AC-MED-074.1). */
  defaultKind?: MedNationalReferralType;
}) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [open, setOpen] = useState(false);
  const canSubmit = access.can('med.national_referral.submit');

  const referrals = useQuery({
    queryKey: referralKeys.referrals(session?.tenantId, encounterId, patientId),
    queryFn: async (): Promise<MedNationalReferralItemView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/national-referrals', { params: { query: { encounterId } } }),
      );
      return (response as MedNationalReferralListResponse).items;
    },
  });

  const create = useMutation({
    mutationFn: (request: MedCreateNationalReferralRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/national-referrals', {
          body: request,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: referralKeys.referrals(session?.tenantId, encounterId, patientId) });
      void queryClient.invalidateQueries({ queryKey: referralKeys.trackingCodes(session?.tenantId, encounterId) });
    },
    onSuccess: () => setOpen(false),
  });

  const columns: DataTableColumn<MedNationalReferralItemView>[] = [
    { id: 'status', headerKey: 'med:referral_national.status', cell: (row) => <ReferralStatusChip status={row.status} /> },
    {
      id: 'kind',
      headerKey: 'med:referral_national.kind',
      cell: (row) => <span data-testid={`med-referral-kind-${row.id}`}>{t(`med:referral_national_kind.${row.kind}`)}</span>,
    },
    {
      id: 'target',
      headerKey: 'med:referral_national.target',
      cell: (row) => <span>{row.toCenterName ?? row.toSpecialtyCode ?? '—'}</span>,
    },
    { id: 'date', headerKey: 'med:referral_national.referred_on', cell: (row) => <span>{toJalali(row.referredOn)}</span> },
    {
      id: 'reserved',
      headerKey: 'med:referral_national.reserved_start',
      cell: (row) => <span>{row.reservedStart ? toJalaliDateTime(row.reservedStart, { timeZone }) : '—'}</span>,
    },
    {
      id: 'code',
      headerKey: 'med:referral_national.tracking_code',
      cell: (row) => (
        <span data-testid={`med-referral-code-${row.id}`}>
          {row.trackingCode ?? (row.errorCode ? `${row.errorCode} · ${t('med:referral_national.attempts', { count: row.attempts })}` : '—')}
        </span>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">{t('med:referral_national.panel_title')}</Typography>
        {canSubmit ? (
          <Button variant="contained" onClick={() => setOpen(true)} data-testid="med-referral-national-new">
            {t('med:referral_national.new')}
          </Button>
        ) : null}
      </Stack>
      <Alert severity="info" data-testid="med-referral-async-hint">
        {t('med:referral_national.async_hint')}
      </Alert>
      <DataTable
        rows={referrals.data}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={referrals.isLoading}
        error={referrals.error}
        onRetry={() => void referrals.refetch()}
        emptyTitleKey="med:referral_national.empty"
        captionKey="med:referral_national.panel_title"
      />
      {open ? (
        <NationalReferralDialog
          encounterId={encounterId}
          defaultKind={defaultKind}
          onClose={() => setOpen(false)}
          onSubmit={(request) => create.mutate(request)}
          isPending={create.isPending}
          error={create.error}
        />
      ) : null}
    </Stack>
  );
}

/** دیالوگ ثبت ارجاع ملی (AC-MED-070.1، 071.2، 074.1). */
function NationalReferralDialog({
  encounterId,
  defaultKind,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  encounterId: string;
  defaultKind: MedNationalReferralType;
  onClose: () => void;
  onSubmit: (request: MedCreateNationalReferralRequest) => void;
  isPending: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const [kind, setKind] = useState<MedNationalReferralType>(defaultKind);
  const [method, setMethod] = useState<MedNationalReferralScheduling>('NoAppointment');
  const [system, setSystem] = useState<ReferralSystem>('SIB');
  const [complaint, setComplaint] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [centerRef, setCenterRef] = useState('');
  const [centerName, setCenterName] = useState('');
  const [message, setMessage] = useState('');
  const [actions, setActions] = useState('');
  const [count, setCount] = useState('1');
  const [referredOn, setReferredOn] = useState('');
  const [slotRef, setSlotRef] = useState('');

  const slots = useQuery({
    queryKey: referralKeys.slots(session?.tenantId, encounterId, specialty, centerRef),
    enabled: method === 'WithAppointment' && specialty.trim().length > 0,
    queryFn: async (): Promise<MedReferralSlotView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/national-referrals/slots', {
          params: { query: { encounterId, specialtyCode: specialty.trim(), centerRef: centerRef.trim() || undefined } },
        }),
      );
      return (response as MedReferralSlotListResponse).items;
    },
  });

  const codingError = problemField(error, 'field');
  const codingReason = problemField(error, 'reason');
  const earliest = problemField(error, 'earliest');

  return (
    <FormDialog
      open
      titleKey="med:referral_national.new"
      submitKey="med:referral_national.submit"
      onClose={onClose}
      isDirty={complaint.length + diagnosis.length + message.length + specialty.length > 0}
      submitError={error}
      onSubmit={() =>
        onSubmit({
          encounterId,
          kind,
          method,
          complaints: complaint.trim() ? [{ code: complaint.trim() }] : [],
          diagnoses: diagnosis.trim() ? [{ code: diagnosis.trim() }] : [],
          actions: actions.trim() || null,
          message: message.trim() || null,
          toSpecialtyCode: specialty.trim() || null,
          toCenterRef: centerRef.trim() || null,
          toCenterName: centerName.trim() || null,
          count: Number(count) || 1,
          referredOn: referredOn || null,
          slotRef: method === 'WithAppointment' ? slotRef || null : null,
          inboundReferralId: null,
          system,
        })
      }
      isSubmitting={isPending}
    >
      <Stack spacing={2}>
        {codingError ? (
          <Alert severity="error" data-testid="med-referral-dialog-error">
            <AlertTitle>{t(`med:referral_national.field.${codingError}`, { defaultValue: codingError })}</AlertTitle>
            {codingReason ? t(`med:referral_national.reason.${codingReason}`, { defaultValue: codingReason }) : null}
            {earliest ? ` ${t('med:referral_national.earliest', { date: toJalali(earliest) })}` : null}
          </Alert>
        ) : null}
        <TextField
          select
          label={t('med:referral_national.kind')}
          value={kind}
          onChange={(event) => setKind(event.target.value as MedNationalReferralType)}
          data-testid="med-referral-kind"
        >
          {(['Level1', 'Horizontal', 'Vertical'] as MedNationalReferralType[]).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:referral_national_kind.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t('med:referral_national.method')}
          value={method}
          onChange={(event) => setMethod(event.target.value as MedNationalReferralScheduling)}
          data-testid="med-referral-method"
        >
          {(['NoAppointment', 'WithAppointment'] as MedNationalReferralScheduling[]).map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:referral_national_method.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t('med:referral_national.system')}
          value={system}
          onChange={(event) => setSystem(event.target.value as ReferralSystem)}
          data-testid="med-referral-system"
        >
          {REFERRAL_SYSTEMS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:referral_national_system.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('med:referral_national.complaint')}
          helperText={t('med:referral_national.complaint_hint')}
          value={complaint}
          onChange={(event) => setComplaint(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-complaint' }}
        />
        <TextField
          label={t('med:referral_national.diagnosis')}
          value={diagnosis}
          onChange={(event) => setDiagnosis(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-diagnosis' }}
        />
        <TextField
          label={t('med:referral_national.actions')}
          value={actions}
          onChange={(event) => setActions(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-actions' }}
        />
        <TextField
          label={t('med:referral_national.specialty')}
          value={specialty}
          onChange={(event) => setSpecialty(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-specialty' }}
        />
        <TextField
          label={t('med:referral_national.center_ref')}
          value={centerRef}
          onChange={(event) => setCenterRef(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-center-ref' }}
        />
        <TextField
          label={t('med:referral_national.center_name')}
          value={centerName}
          onChange={(event) => setCenterName(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-center-name' }}
        />
        <TextField
          label={t('med:referral_national.count')}
          value={count}
          onChange={(event) => setCount(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-count', inputMode: 'numeric' }}
        />
        <BusinessDateField
          label={t('med:referral_national.referred_on')}
          value={referredOn}
          onChange={setReferredOn}
          testId="med-referral-referred-on"
        />
        <TextField
          label={t('med:referral_national.message')}
          multiline
          minRows={2}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          inputProps={{ 'data-testid': 'med-referral-message' }}
        />
        {method === 'WithAppointment' ? (
          <TextField
            select
            label={t('med:referral_national.slot')}
            helperText={t('med:referral_national.slot_hint')}
            value={slotRef}
            onChange={(event) => setSlotRef(event.target.value)}
            data-testid="med-referral-slot"
            disabled={slots.isLoading}
          >
            {(slots.data ?? []).map((slot) => (
              <MenuItem key={slot.slotRef} value={slot.slotRef}>
                {`${toJalaliDateTime(slot.start, { timeZone })} · ${slot.centerName ?? slot.centerRef} · ${slot.receptionLocation ?? ''}`}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
      </Stack>
    </FormDialog>
  );
}

/**
 * جزئیات ارجاع ورودی ملی و ثبت بازخورد (US-MED-072، 073). پیام، شکایت و تشخیص سطح ۱ **Clinical**‌اند و هر مشاهده با
 * `RecordView` در سرور ثبت می‌شود (BR-MED-078).
 */
export function InboundNationalReferralPanel({
  inboundReferralId,
  encounterId,
}: {
  inboundReferralId: string;
  encounterId: string;
}) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const canSubmit = access.can('med.national_referral.submit');

  const detail = useQuery({
    queryKey: referralKeys.inbound(session?.tenantId, inboundReferralId),
    enabled: canSubmit,
    queryFn: async (): Promise<MedInboundNationalReferralView> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/inbound-referrals/{id}/national', { params: { path: { id: inboundReferralId } } }),
      )) as MedInboundNationalReferralView,
  });

  const inquire = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/inbound-referrals/{id}:inquire', {
          params: { path: { id: inboundReferralId } },
          body: { encounterId, system: null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: referralKeys.inbound(session?.tenantId, inboundReferralId) });
    },
  });

  if (!canSubmit) {
    return null;
  }

  const view = detail.data;
  const inquiryError = problemField(inquire.error, 'reason');

  return (
    <Stack spacing={2} data-testid="med-inbound-national-panel">
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">{t('med:referral_inbound.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => inquire.mutate()} disabled={inquire.isPending} data-testid="med-inbound-inquire">
            {t('med:referral_inbound.inquire')}
          </Button>
          {view?.inquiredAt && !view.feedbackSubmitted ? (
            <Button variant="contained" onClick={() => setFeedbackOpen(true)} data-testid="med-inbound-feedback">
              {t('med:referral_inbound.feedback')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {inquiryError ? (
        <Alert severity="error" data-testid="med-inbound-inquire-error">
          {t(`med:referral_inbound.invalid_code.${inquiryError}`, { defaultValue: t('med:referral_inbound.invalid_code.notfound') })}
        </Alert>
      ) : null}
      {view?.feedbackSubmitted ? (
        <Alert severity="success" data-testid="med-inbound-feedback-done">
          {t('med:referral_inbound.feedback_done')}
        </Alert>
      ) : (
        <Alert severity="warning" data-testid="med-inbound-feedback-required">
          {t('med:referral_inbound.feedback_required')}
        </Alert>
      )}
      {view?.inquiredAt ? (
        <Stack spacing={0.5} data-testid="med-inbound-detail">
          <Typography variant="body2">
            {t('med:referral_inbound.code')}: {view.nationalReferralCode ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('med:referral_inbound.from')}: {view.fromProviderName ?? '—'} · {view.fromSpecialtyCode ?? '—'}
          </Typography>
          <Typography variant="body2">
            {t('med:referral_inbound.referred_on')}: {view.referredOn ? toJalali(view.referredOn) : '—'}
          </Typography>
          <Typography variant="body2">
            {t('med:referral_inbound.inquired_at')}: {toJalaliDateTime(view.inquiredAt, { timeZone })}
          </Typography>
          <Typography variant="body2" data-testid="med-inbound-message">
            {t('med:referral_inbound.message')}: {view.message ?? '—'}
          </Typography>
          <Typography variant="body2" data-testid="med-inbound-complaints">
            {t('med:referral_inbound.complaints')}: {view.complaints.join(t('common:list_separator')) || '—'}
          </Typography>
          <Typography variant="body2" data-testid="med-inbound-diagnoses">
            {t('med:referral_inbound.diagnoses')}: {view.diagnoses.join(t('common:list_separator')) || '—'}
          </Typography>
        </Stack>
      ) : null}
      {feedbackOpen ? (
        <ReferralFeedbackDialog
          encounterId={encounterId}
          inboundReferralId={inboundReferralId}
          onClose={() => setFeedbackOpen(false)}
        />
      ) : null}
    </Stack>
  );
}

/** دیالوگ بازخورد ارجاع (AC-MED-073.2؛ پرسش‌ها از خود سامانه می‌آیند، نه مقدار ثابت). */
function ReferralFeedbackDialog({
  encounterId,
  inboundReferralId,
  onClose,
}: {
  encounterId: string;
  inboundReferralId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [comments, setComments] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const form = useQuery({
    queryKey: referralKeys.feedbackForm(session?.tenantId, encounterId, 'SIB'),
    queryFn: async (): Promise<MedReferralFeedbackFormResponse> =>
      (await unwrap(
        tenantApi.GET('/api/v1/medical-orders/national-referrals/feedback-form', { params: { query: { encounterId } } }),
      )) as MedReferralFeedbackFormResponse,
  });

  const submit = useMutation({
    mutationFn: (request: MedSubmitReferralFeedbackRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referral-feedbacks', {
          body: request,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: referralKeys.inbound(session?.tenantId, inboundReferralId) });
      void queryClient.invalidateQueries({ queryKey: referralKeys.trackingCodes(session?.tenantId, encounterId) });
    },
    onSuccess: onClose,
  });

  const immutable = isApiError(submit.error) && submit.error.code === 'PHX-MED-0305';

  return (
    <FormDialog
      open
      titleKey="med:referral_inbound.feedback"
      submitKey="med:referral_inbound.feedback_submit"
      onClose={onClose}
      isDirty={comments.length > 0 || Object.keys(answers).length > 0}
      submitError={submit.error}
      onSubmit={() =>
        submit.mutate({
          encounterId,
          inboundReferralId,
          comments: comments.trim() || null,
          answers: Object.entries(answers).map(([questionCode, answerCode]) => ({
            questionCode,
            answerCode,
            comment: notes[questionCode]?.trim() || null,
          })),
          prescribedItems: [],
          system: null,
        })
      }
      isSubmitting={submit.isPending}
    >
      <Stack spacing={2}>
        {immutable ? (
          <Alert severity="error" data-testid="med-feedback-immutable">
            {t('med:referral_inbound.feedback_immutable')}
          </Alert>
        ) : null}
        {form.data?.isSample ? <SampleBadge /> : null}
        {(form.data?.questions ?? []).map((question) => (
          <Stack key={question.code} spacing={1}>
            <TextField
              select
              label={question.text}
              value={answers[question.code] ?? ''}
              onChange={(event) => setAnswers((previous) => ({ ...previous, [question.code]: event.target.value }))}
              data-testid={`med-feedback-answer-${question.code}`}
            >
              {question.answers.map((answer) => (
                <MenuItem key={answer.code} value={answer.code}>
                  {answer.text}
                </MenuItem>
              ))}
            </TextField>
            {question.answers.find((answer) => answer.code === answers[question.code])?.requiresComment ? (
              <TextField
                label={t('med:referral_inbound.answer_comment')}
                value={notes[question.code] ?? ''}
                onChange={(event) => setNotes((previous) => ({ ...previous, [question.code]: event.target.value }))}
                inputProps={{ 'data-testid': `med-feedback-comment-${question.code}` }}
              />
            ) : null}
          </Stack>
        ))}
        <TextField
          label={t('med:referral_inbound.comments')}
          multiline
          minRows={2}
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          inputProps={{ 'data-testid': 'med-feedback-comments' }}
        />
      </Stack>
    </FormDialog>
  );
}

/** پنل دستور بستری سرپایی یک مواجهه (US-MED-075، 098). */
export function AdmissionOrderPanel({ encounterId }: { encounterId: string }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [open, setOpen] = useState(false);
  const canSubmit = access.can('med.national_referral.submit');

  const orders = useQuery({
    queryKey: referralKeys.admissionOrders(session?.tenantId, encounterId),
    queryFn: async (): Promise<MedAdmissionOrderView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/admission-orders', { params: { query: { encounterId } } }),
      );
      return (response as MedAdmissionOrderListResponse).items;
    },
  });

  const hospitals = useQuery({
    queryKey: referralKeys.hospitals(session?.tenantId),
    enabled: open && canSubmit,
    queryFn: async () => unwrap(tenantApi.GET('/api/v1/medical-orders/insurer-referrals/hospitals', {})),
  });

  const create = useMutation({
    mutationFn: (request: MedSubmitAdmissionOrderRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/admission-orders', {
          body: request,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: referralKeys.admissionOrders(session?.tenantId, encounterId) });
      void queryClient.invalidateQueries({ queryKey: referralKeys.trackingCodes(session?.tenantId, encounterId) });
    },
    onSuccess: () => setOpen(false),
  });

  const columns: DataTableColumn<MedAdmissionOrderView>[] = [
    { id: 'status', headerKey: 'med:referral_national.status', cell: (row) => <ReferralStatusChip status={row.status} /> },
    { id: 'hospital', headerKey: 'med:admission.hospital', cell: (row) => <span>{row.hospitalName ?? row.hospitalRef ?? '—'}</span> },
    { id: 'admit', headerKey: 'med:admission.admit_on', cell: (row) => <span>{toJalali(row.admitOn)}</span> },
    {
      id: 'code',
      headerKey: 'med:referral_national.tracking_code',
      cell: (row) => <span data-testid={`med-admission-code-${row.id}`}>{row.trackingCode ?? row.errorCode ?? '—'}</span>,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">{t('med:admission.title')}</Typography>
        {canSubmit ? (
          <Button variant="contained" onClick={() => setOpen(true)} data-testid="med-admission-new">
            {t('med:admission.new')}
          </Button>
        ) : null}
      </Stack>
      <Alert severity="info" data-testid="med-admission-scope-hint">
        {t('med:admission.scope_hint')}
      </Alert>
      <DataTable
        rows={orders.data}
        columns={columns}
        getRowId={(row) => row.id}
        isLoading={orders.isLoading}
        error={orders.error}
        onRetry={() => void orders.refetch()}
        emptyTitleKey="med:admission.empty"
        captionKey="med:admission.title"
      />
      {open ? (
        <AdmissionOrderDialog
          encounterId={encounterId}
          hospitals={((hospitals.data as { items?: { siamId: string; name: string }[] } | undefined)?.items ?? []).map((item) => item)}
          onClose={() => setOpen(false)}
          onSubmit={(request) => create.mutate(request)}
          isPending={create.isPending}
          error={create.error}
        />
      ) : null}
    </Stack>
  );
}

/** دیالوگ دستور بستری (AC-MED-075.1، AC-MED-098.1). */
function AdmissionOrderDialog({
  encounterId,
  hospitals,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  encounterId: string;
  hospitals: { siamId: string; name: string }[];
  onClose: () => void;
  onSubmit: (request: MedSubmitAdmissionOrderRequest) => void;
  isPending: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('med');
  const [hospitalRef, setHospitalRef] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [message, setMessage] = useState('');
  const [admitOn, setAdmitOn] = useState('');
  const [system, setSystem] = useState<ReferralSystem>('SIB');
  const earliest = problemField(error, 'earliest');
  const field = problemField(error, 'field');

  return (
    <FormDialog
      open
      titleKey="med:admission.new"
      submitKey="med:admission.submit"
      onClose={onClose}
      isDirty={hospitalRef.length + diagnosis.length + message.length + admitOn.length > 0}
      submitError={error}
      onSubmit={() =>
        onSubmit({
          encounterId,
          hospitalRef: hospitalRef || null,
          hospitalName: hospitals.find((item) => item.siamId === hospitalRef)?.name ?? null,
          diagnoses: diagnosis.trim() ? [{ code: diagnosis.trim() }] : [],
          message: message.trim() || null,
          admitOn: admitOn || null,
          system,
        })
      }
      isSubmitting={isPending}
    >
      <Stack spacing={2}>
        {field ? (
          <Alert severity="error" data-testid="med-admission-error">
            {t(`med:referral_national.field.${field}`, { defaultValue: field })}
            {earliest ? ` · ${t('med:referral_national.earliest', { date: toJalali(earliest) })}` : null}
          </Alert>
        ) : null}
        <TextField
          select
          label={t('med:admission.hospital')}
          helperText={t('med:admission.hospital_hint')}
          value={hospitalRef}
          onChange={(event) => setHospitalRef(event.target.value)}
          data-testid="med-admission-hospital"
        >
          {hospitals.map((item) => (
            <MenuItem key={item.siamId} value={item.siamId}>
              {item.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('med:referral_national.diagnosis')}
          value={diagnosis}
          onChange={(event) => setDiagnosis(event.target.value)}
          inputProps={{ 'data-testid': 'med-admission-diagnosis' }}
        />
        <BusinessDateField
          label={t('med:admission.admit_on')}
          value={admitOn}
          onChange={setAdmitOn}
          testId="med-admission-admit-on"
        />
        <TextField
          label={t('med:referral_national.message')}
          multiline
          minRows={2}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          inputProps={{ 'data-testid': 'med-admission-message' }}
        />
        <TextField
          select
          label={t('med:referral_national.system')}
          value={system}
          onChange={(event) => setSystem(event.target.value as ReferralSystem)}
          data-testid="med-admission-system"
        >
          {REFERRAL_SYSTEMS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`med:referral_national_system.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </FormDialog>
  );
}

/**
 * جستجوی کد رهگیری و کدهای مواجهه انتخاب‌شده (AC-MED-076.2): هم تب «کد رهگیری» SCR-STAFF-705 و هم تب هم‌نام
 * SCR-STAFF-707 همین پنل را نشان می‌دهند تا رفتار جستجو یک جا بماند.
 */
export function TrackingCodeSearchPanel({
  encounterId,
  onPick,
}: {
  /** مواجهه جاری (تهی = فقط جستجو). */
  encounterId?: string;
  /** انتخاب مواجهه یافته‌شده؛ تهی = پیوند به SCR-STAFF-707. */
  onPick?: (encounterId: string) => void;
}) {
  const { t } = useTranslation('med');
  const [code, setCode] = useState('');
  const search = useTrackingCodeSearch(code);

  return (
    <Stack spacing={2}>
      <TextField
        label={t('med:referral_national.search_code')}
        helperText={t('med:referral_national.search_hint')}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        inputProps={{ 'data-testid': 'med-tracking-search' }}
        sx={{ maxWidth: 360 }}
      />
      {search.data ? (
        search.data.length > 0 ? (
          <Stack spacing={0.5} data-testid="med-tracking-search-results">
            <Typography variant="body2">
              {t('med:referral_national.search_found', { count: search.data.length })}
            </Typography>
            {search.data.map((found) =>
              onPick ? (
                <Button
                  key={found}
                  size="small"
                  onClick={() => onPick(found)}
                  sx={{ alignSelf: 'flex-start' }}
                  data-testid={`med-tracking-found-${found}`}
                >
                  {faDigits(found.slice(0, 8))}
                </Button>
              ) : (
                <RouterLink key={found} to={`/desk/national-referrals?encounterId=${found}`}>
                  {faDigits(found.slice(0, 8))}
                </RouterLink>
              ),
            )}
          </Stack>
        ) : (
          <Alert severity="info" data-testid="med-tracking-search-empty">
            {t('med:referral_national.search_empty')}
          </Alert>
        )
      ) : null}
      {encounterId ? <TrackingCodesPanel encounterId={encounterId} /> : null}
    </Stack>
  );
}

/** جستجوی مواجهه با کد رهگیری (AC-MED-076.2). */
export function useTrackingCodeSearch(code: string) {
  const { tenantApi, session } = useTenantSession();
  const trimmed = code.trim();

  return useQuery({
    queryKey: referralKeys.trackingSearch(session?.tenantId, trimmed),
    enabled: trimmed.length > 2,
    queryFn: async (): Promise<string[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/medical-orders/tracking-codes/{code}/encounters', { params: { path: { code: trimmed } } }),
      );
      return ((response as { items?: string[] }).items ?? []) as string[];
    },
  });
}

/** شمار کدهای رهگیری هر نوع (نمای خلاصه SCR-STAFF-707). */
export function useTrackingCodeSummary(encounterId: string | undefined) {
  const codes = useTrackingCodes(encounterId);

  return useMemo(() => {
    const items = codes.data ?? [];
    return {
      total: items.length,
      byKind: items.reduce<Record<string, number>>((accumulator, item) => {
        accumulator[item.kind] = (accumulator[item.kind] ?? 0) + 1;
        return accumulator;
      }, {}),
      label: faDigits(items.length),
      query: codes,
      api: API,
    };
  }, [codes]);
}
